import Image from 'next/legacy/image'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import styled from 'styled-components'

import ENV_VARS from '@/config'
import { breakpoints, colors } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'
import { Button } from '@/components/button-new'
import { LivLogoIcon } from '@/components/icons'
import { H1, ParagraphLarge } from '@/components/text'

import titleImage from './images/title-image.png'

const { DASHBOARD_URL } = ENV_VARS

const Section = styled.section<{ maxWidth: string }>`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  max-width: ${props => props.maxWidth};

  @media (max-width: ${breakpoints.desktop}) {
    justify-content: center;
  }
`

const Wrapper = styled.div`
  margin: 36px 72px;
  max-width: 468px;
  display: flex;
  flex-direction: column;

  @media (max-width: ${breakpoints.desktop}) {
    position: static;
    margin: 36px 32px;
    max-width: 626px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    margin: 16px 16px 32px;
  }

  > a:first-of-type {
    position: fixed;
    top: 36px;
    left: 72px;
    @media (max-width: ${breakpoints.desktop}) {
      position: static;
      width: 32px;
      height: 32px;
    }
  }
`

const Title = styled(H1)<{ large?: boolean }>`
  color: ${colors.black};
  margin: 0 0 16px;

  @media (max-width: 1200px) {
    font-size: 32px;
    line-height: 40px;
  }

  @media (max-width: ${breakpoints.desktop}) {
    font-size: 32px;
    line-height: 40px;
    margin-bottom: 8px;
  }

  ${props =>
    props.large &&
    `
      font-size: 48px;
      line-height: 56px;

      @media (max-width: ${breakpoints.desktop}) {
        font-size: 40px;
        line-height: 48px;
      }
  `}
`

const Description = styled(ParagraphLarge)`
  color: ${colors.grey56};
  margin: 0 0 24px;

  @media (max-width: ${breakpoints.desktop}) {
    font-size: 16px;
    line-height: 24px;
  }
`

const LogoSpacer = styled.div`
  height: 104px;
  @media (max-width: ${breakpoints.desktop}) {
    height: 32px;
  }
`

const Spacer = styled.div`
  height: 60px;
  @media (max-width: 1200px) {
    display: none;
  }
`

const ImageWrapper = styled.div`
  max-width: 360px;
  margin-bottom: 32px;

  @media (max-width: ${breakpoints.desktop}) {
    display: none;
  }
`

const LogoWrapper = styled.a``

type Plan = {
  title: string
  description: string
}

interface Props {
  accountType: string
  isConfirmation?: boolean
  customTitles?: Record<string, string>
}

const TitleSection: React.FC<Props> = ({
  accountType,
  isConfirmation,
  customTitles
}) => {
  const { t } = useTranslation('pricing')
  const displayTablet = useMediaQuery(`(max-width: ${breakpoints.desktop})`)

  const REGISTER: Record<string, Plan> = {
    essentials: {
      title: t(
        `pricing:You're on your way to upgrading how you rent with our Essentials plan`
      ),
      description: t(
        'pricing:Get started for free with unlimited listings and basic rental management features'
      )
    },
    growth: {
      title: t(
        `pricing:You're on your way to upgrading how you rent with our Growth plan`
      ),
      description: t(
        'pricing:Streamline your operations and grow your portfolio with enhanced rental marketing and management tools'
      )
    },
    business: {
      title: t(
        `pricing:You're on your way to upgrading how you rent with our Business plan`
      ),
      description: t(
        'pricing:Scale your property management business with our all-in-one marketing & management software'
      )
    }
  }

  const title = isConfirmation
    ? t('pricing:Welcome to the next step in your liv.rent journey')
    : customTitles?.title || REGISTER[accountType]?.title
  const description = isConfirmation
    ? t(
        `pricing:You'll receive an email shortly with your payment and subscription details. Create your first listing to take advantage of your new set of rental management tools.`
      )
    : customTitles?.description || REGISTER[accountType]?.description

  return (
    <Section maxWidth={isConfirmation ? '600px' : 'unset'}>
      <Wrapper>
        <Link
          href={customTitles?.homeLink || (isConfirmation ? '/' : '/pricing')}
          passHref
          legacyBehavior
        >
          <LogoWrapper
            aria-label={
              isConfirmation
                ? t(`pricing:Back home`)
                : t(`pricing:Back to pricing`)
            }
          >
            <LivLogoIcon
              width={displayTablet ? 64 : 80}
              height={displayTablet ? 64 : 80}
            />
          </LogoWrapper>
        </Link>
        <LogoSpacer aria-hidden />
        {isConfirmation ? (
          <Spacer aria-hidden />
        ) : (
          <ImageWrapper>
            <Image
              src={titleImage}
              alt=""
              layout="responsive"
              sizes="360px"
              width={360}
              height={188}
              placeholder="blur"
            />
          </ImageWrapper>
        )}
        <Title as="h1" large={isConfirmation}>
          {title}
        </Title>
        <Description>{description}</Description>
        {isConfirmation && (
          <Button href={`${DASHBOARD_URL}`} width={170}>
            {t('pricing:Go to Dashboard')}
          </Button>
        )}
      </Wrapper>
    </Section>
  )
}

export default TitleSection
